import { Alert, Button, Select, Tabs, TabsProps } from "antd";
import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  useGetExpenseAnalysisByCategoryFor6Months2,
  useGetExpenseAnalysisBySubCategory2For6Months,
  useGetExpensesTillDate,
  useGetGroups2,
} from "../api/api.get";
import GroupDialog from "../components/dialog/group.dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { useCurrencyContext } from "../providers/currency.provider";
import { useGroupContext } from "../providers/groups.provider";
import { capitalize } from "../utils/functions";
import { toFixedDisplay } from "../utils/number.utils";

export default function AnalysisPage() {
  const { data } = useGetGroups2();
  const { defaultGroupId, changeDefaultGroupId } = useGroupContext();
  const [groupId, setGroupId] = useState<string | undefined>(defaultGroupId);

  const handleChangeGroup = () => {
    changeDefaultGroupId(groupId);
  };

  const [groupsDialogVisible, showGroupsDialog] = useState(false);

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "Gross Total",
      children: <MonthlyTotalTab />,
    },
    {
      key: "2",
      label: "Category",
      children: <CategoryTab />,
    },
    {
      key: "3",
      label: "SubCategory",
      children: <SubCategoryTab />,
    },
  ];

  return (
    <div className="p-5 mt-4 mb-10">
      {groupsDialogVisible && (
        <GroupDialog
          open={groupsDialogVisible}
          onCancel={() => showGroupsDialog(false)}
          onOk={() => showGroupsDialog(false)}
        />
      )}

      <Tabs defaultActiveKey="1" items={items} />

      <div>
        {!defaultGroupId && (
          <div className="flex flex-col justify-center items-center">
            <Alert title="No default group set" type="error" showIcon />
            <div className="flex mt-4 gap-5">
              <Select
                style={{ width: 300 }}
                placeholder="Select a default group"
                onChange={(value) => setGroupId(value)}
                value={groupId}
              >
                {data?.map((group) => (
                  <Select.Option key={group.id} value={group.id}>
                    {group.name}
                  </Select.Option>
                ))}
              </Select>
              <Button type="primary" onClick={() => handleChangeGroup()}>
                Go
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MonthlyTotalTab() {
  const { defaultGroupId } = useGroupContext();

  const { data: totalData } = useGetExpensesTillDate(defaultGroupId, 6);

  const monthlyTrend = useMemo(() => {
    if (!totalData) return [];
    return totalData
      .slice(0, 6)
      .map((item: any) => ({
        month: capitalize(item.monthName),
        spending: item.total,
      }))
      .reverse();
  }, [totalData]);

  return (
    <Card className="bg-white my-10">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">Monthly Trend</CardTitle>
        <CardDescription>Till Date - Last 6 months spending</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={monthlyTrend}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip formatter={(value) => `$${value}`} />
            <Line
              type="monotone"
              dataKey="spending"
              stroke="#5B5BFF"
              strokeWidth={2}
              dot={{ fill: "#5B5BFF", r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function CategoryTab() {
  const { defaultGroupId } = useGroupContext();
  const { currency } = useCurrencyContext();

  const { data: categoryData } = useGetExpenseAnalysisByCategoryFor6Months2(
    defaultGroupId || "",
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 mt-10 mb-8">
      {categoryData &&
        categoryData?.map((category: any) => (
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {currency}
                {toFixedDisplay(category.total)}
              </CardTitle>

              <CardDescription>
                {capitalize(category.monthName)} {category.year}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {category.data.map((item: any) => (
                  <div className="border-b pb-4">
                    <p className="text-sm text-muted-foreground mb-1">
                      {capitalize(item.category)}
                    </p>
                    <p className="text-l font-bold text-foreground">
                      {currency}
                      {toFixedDisplay(item.amount)}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
    </div>
  );
}

function SubCategoryTab() {
  const { defaultGroupId } = useGroupContext();
  const { currency } = useCurrencyContext();

  const { data: categoryData } =
    useGetExpenseAnalysisBySubCategory2For6Months(defaultGroupId);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 mt-10 mb-8">
      {categoryData &&
        categoryData?.map((category: any) => (
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {currency}
                {toFixedDisplay(category.total)}
              </CardTitle>

              <CardDescription>
                {capitalize(category.monthName)} {category.year}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {category.data.map((item: any) => (
                  <div className="border-b pb-4">
                    <p className="text-sm text-muted-foreground mb-1">
                      {capitalize(item.subcategory)}
                    </p>
                    <p className="text-l font-bold text-foreground">
                      {currency}
                      {toFixedDisplay(item.amount)}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
    </div>
  );
}

export function CategoryGraphTab() {
  const { defaultGroupId } = useGroupContext();

  const { data: categoryData } = useGetExpenseAnalysisByCategoryFor6Months2(
    defaultGroupId || "",
  );

  // give some nice vibrant colors for this
  const COLORS = [
    "#06B6D4", // Cyan
    "#22C55E", // Emerald
    "#F97316", // Orange
    "#E11D48", // Rose
    "#8B5CF6", // Purple
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 mb-8">
      {categoryData &&
        categoryData.map((monthData: any) => {
          const mapData = monthData.data.map((item: any) => ({
            name: item.category,
            value: isNaN(item.amount) ? 0 : +item.amount,
          }));

          return (
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {monthData.monthName} {monthData.year}
                </CardTitle>
                <CardDescription>{monthData.total}</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={mapData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: $${value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {mapData.map((entry: any, index: number) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `$${value}`} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          );
        })}
    </div>
  );
}
